// src/admin/AdminShipping.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';

function AdminShipping() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Édition
  const [editingId, setEditingId] = useState(null);
  const [editFee, setEditFee] = useState('');
  const [saving, setSaving] = useState(false);

  // Ajout
  const [showForm, setShowForm] = useState(false);
  const [newCity, setNewCity] = useState('');
  const [newFee, setNewFee] = useState('');
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);

  const token = () => localStorage.getItem('auth_token');

  /* =============================
     CHARGER
  ============================= */
  const fetchZones = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('https://madadline.com/api/shipping', {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setZones(Array.isArray(data) ? data : []);
    } catch {
      setError('Impossible de charger les zones de livraison.');
      setZones([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchZones(); }, []);

  /* =============================
     ÉDITION
  ============================= */
  const startEdit = (zone) => {
    setEditingId(zone.id);
    setEditFee(zone.shipping_cost);
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await axios.put(
        `https://madadline.com/api/shipping/${id}`,
        { shipping_cost: Number(editFee) },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      await fetchZones();
      setEditingId(null);
      setMessage({ type: 'success', text: '✓ Tarif mis à jour' });
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de la modification.' });
    } finally {
      setSaving(false);
    }
  };

  /* =============================
     AJOUT
  ============================= */
  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!newCity.trim()) {
      setMessage({ type: 'error', text: 'Le nom de la ville est obligatoire.' });
      return;
    }
    if (!newFee || Number(newFee) < 0) {
      setMessage({ type: 'error', text: 'Le frais doit être un nombre positif.' });
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        'https://madadline.com/api/shipping',
        { country: newCity.trim(), shipping_cost: Number(newFee) },
        { headers: { Authorization: `Bearer ${token()}` } }
      );

      setMessage({ type: 'success', text: '✓ Ville ajoutée' });
      setNewCity('');
      setNewFee('');
      setShowForm(false);
      await fetchZones();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      const serverErrors = err.response?.data?.errors;
      const firstError = serverErrors
        ? Object.values(serverErrors)[0]?.[0]
        : (err.response?.data?.error || 'Erreur lors de la création.');
      setMessage({ type: 'error', text: firstError });
    } finally {
      setCreating(false);
    }
  };

  /* =============================
     SUPPRESSION
  ============================= */
  const handleDelete = async (zone) => {
    if (zone.country === 'Autre' || zone.country === 'Autres villes') {
      alert('Impossible de supprimer la zone par défaut.');
      return;
    }
    if (!window.confirm(`Supprimer "${zone.country}" ?`)) return;

    try {
      await axios.delete(`https://madadline.com/api/shipping/${zone.id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      await fetchZones();
    } catch {
      alert('Erreur lors de la suppression.');
    }
  };

  /* =============================
     RENDER
  ============================= */
  return (
    <div className="admin-shipping">
      <header className="admin-page-head">
        <div>
          <h1>Frais de livraison</h1>
          <p>{zones.length} zone{zones.length > 1 ? 's' : ''}</p>
        </div>
        <button
          className="admin-btn admin-btn--primary"
          onClick={() => { setShowForm(!showForm); setMessage(null); }}
        >
          {showForm ? '✕ Fermer' : '+ Nouvelle ville'}
        </button>
      </header>

      {message && (
        <div className={`admin-alert admin-alert--${message.type}`}>
          {message.text}
        </div>
      )}

      {/* FORMULAIRE D'AJOUT */}
      {showForm && (
        <div className="admin-card">
          <div className="admin-card__head">
            <h2>Ajouter une ville / zone</h2>
          </div>

          <form onSubmit={handleCreate}>
            <div className="admin-form__grid">
              <div className="admin-form__group">
                <label>Nom de la ville *</label>
                <input
                  type="text"
                  value={newCity}
                  onChange={e => setNewCity(e.target.value)}
                  placeholder="Ex: Berrechid, El Jadida..."
                  required
                />
              </div>
              <div className="admin-form__group">
                <label>Frais (DH) *</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={newFee}
                  onChange={e => setNewFee(e.target.value)}
                  placeholder="40"
                  required
                />
              </div>
            </div>

            <div className="admin-form__actions">
              <button
                type="submit"
                className="admin-btn admin-btn--primary"
                disabled={creating}
              >
                {creating ? 'Ajout en cours…' : 'Ajouter la ville'}
              </button>
              <button
                type="button"
                className="admin-btn"
                onClick={() => { setShowForm(false); setNewCity(''); setNewFee(''); }}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* LISTE */}
      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : error ? (
        <div className="admin-empty" style={{ color: '#c00' }}>{error}</div>
      ) : zones.length === 0 ? (
        <div className="admin-empty">
          <p>Aucune zone de livraison pour le moment.</p>
          <button
            className="admin-btn admin-btn--primary"
            onClick={() => setShowForm(true)}
          >
            Créer la première zone
          </button>
        </div>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Ville / Zone</th>
                <th>Frais (DH)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {zones.map(z => {
                const isDefault = z.country === 'Autre' || z.country === 'Autres villes';
                return (
                  <tr key={z.id}>
                    <td>
                      <strong>{z.country}</strong>
                      {isDefault && (
                        <span className="admin-badge admin-badge--warning" style={{ marginLeft: 8 }}>
                          Défaut
                        </span>
                      )}
                    </td>
                    <td>
                      {editingId === z.id ? (
                        <input
                          type="number"
                          value={editFee}
                          onChange={e => setEditFee(e.target.value)}
                          className="admin-input"
                          style={{ width: 100 }}
                          min="0"
                        />
                      ) : (
                        <strong>{Number(z.shipping_cost).toFixed(0)} DH</strong>
                      )}
                    </td>
                    <td>
                      <div className="admin-table__actions">
                        {editingId === z.id ? (
                          <>
                            <button
                              onClick={() => saveEdit(z.id)}
                              disabled={saving}
                              className="admin-btn-sm admin-btn-sm--edit"
                            >
                              {saving ? '…' : 'Enregistrer'}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="admin-btn-sm"
                            >
                              Annuler
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(z)}
                              className="admin-btn-sm admin-btn-sm--edit"
                            >
                              Modifier
                            </button>
                            {!isDefault && (
                              <button
                                onClick={() => handleDelete(z)}
                                className="admin-btn-sm admin-btn-sm--danger"
                              >
                                Supprimer
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminShipping;